import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {IInstitutionSettingsForm} from '@app/auth-config/models/institution-settings.form';
import {IInstitutionSettingsRequest} from '@app/auth-config/models/institution-settings.request.model';
import {updateInstitution} from '@app/auth-config/state/institution-settings.action';
import {alphanumericWithSpecialCharactersAndDiacritics_Regex, MinMaxChars_Regex} from '@app/shared/constants';
import {ICity} from '@app/shared/models/city.model';
import {ICountry} from '@app/shared/models/country.model';
import {IOrganizationState} from '@app/shared/models/header.model';
import {IRegion} from '@app/shared/models/region.model';
import {ValidationService} from '@app/shared/services/validation.service';
import {selectCustomerOrganization} from '@app/shared/state/header/header.selector';
import {
  loadCitiesFromInstitutionsSettings,
  loadCountriesFromInstitutionsSettings,
  loadRegionsFromInstitutionsSettings,
  resetCities,
} from '@app/signUp/state/sign-up.actions';
import {selectCities, selectCountries, selectRegions} from '@app/signUp/state/sign-up.selector';
import {Store} from '@ngrx/store';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {map, Observable, Subject, takeUntil, tap} from 'rxjs';
import { Bind } from 'primeng/bind';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { Select } from 'primeng/select';
import { Button } from 'primeng/button';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-institution-settings',
    templateUrl: './institution-settings.component.html',
    styleUrl: './institution-settings.component.scss',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        Bind,
        InputText,
        Message,
        Select,
        Button,
        AsyncPipe,
        TranslatePipe,
    ],
})
export class InstitutionSettingsComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly formBuilder = inject(FormBuilder);
  private readonly validationService = inject(ValidationService);
  private readonly translateService = inject(TranslateService);

  private readonly unsubscribe$ = new Subject<void>();
  institutionSettingsForm: FormGroup<IInstitutionSettingsForm>;
  countries$: Observable<ICountry[]>;
  regions$: Observable<IRegion[]>;
  cities$: Observable<ICity[]>;
  selectedOrganization: IOrganizationState | undefined;

  exceedTestLimit = '';
  allowCharecter = '';
  allowAlphanumeric = '';
  allowDigitChar = '';
  allowLetterDiacritics = '';
  allowAlphaNumericDiacritics = '';

  constructor() {
    this.institutionSettingsForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.pattern(alphanumericWithSpecialCharactersAndDiacritics_Regex)]],

      address: [
        '',
        [
          Validators.required,
          this.validationService.alphanumericWithSpecialCharactersAndDiacritics,
          Validators.pattern(MinMaxChars_Regex),
        ],
      ],
      countryId: new FormControl<number | null>(null, Validators.required),
      regionId: new FormControl<number | null>(null, Validators.required),
      cityId: new FormControl<number | null>(null, Validators.required),
      zipPostalCode: ['', [Validators.required, this.validationService.alphanumericCharacters]],
      institutionId: [''],
      contractNo: [''],
    });

    this.countries$ = this.store.select(selectCountries).pipe(
      takeUntil(this.unsubscribe$),
      map(countries => {
        return countries ?? [];
      })
    );
    this.regions$ = this.store.select(selectRegions).pipe(
      takeUntil(this.unsubscribe$),
      map(regions => {
        return regions ?? [];
      })
    );
    this.cities$ = this.store.select(selectCities).pipe(
      takeUntil(this.unsubscribe$),
      map(cities => {
        return cities ?? [];
      })
    );

    this.translateStaticText();
  }

  ngOnInit(): void {
    this.selectCustomerOrganizationAndInitializeForm();
  }

  private selectCustomerOrganizationAndInitializeForm() {
    this.store
      .select(selectCustomerOrganization)
      .pipe(
        tap(customerOrganization => {
          const currentOrganization = customerOrganization?.organization;
          if (currentOrganization) {
            this.store.dispatch(loadCountriesFromInstitutionsSettings());
            this.store.dispatch(
              loadRegionsFromInstitutionsSettings({
                countryId: currentOrganization?.countryId ?? 0,
              })
            );
            this.store.dispatch(
              loadCitiesFromInstitutionsSettings({
                countryId: currentOrganization?.countryId ?? 0,
                regionId: currentOrganization?.regionId ?? 0,
              })
            );

            this.institutionSettingsForm.patchValue({
              name: currentOrganization.name,
              address: currentOrganization.address,
              countryId: currentOrganization.countryId,
              regionId: currentOrganization.regionId,
              cityId: currentOrganization.cityId,
              zipPostalCode: currentOrganization.zipPostalCode,
              institutionId: currentOrganization.instituteId,
              contractNo: currentOrganization.contractNo,
            });

            this.selectedOrganization = currentOrganization;
          }
        })
      )
      .subscribe();
  }

  onChangeCountry() {
    const selectedCountryId = this.institutionSettingsForm.controls.countryId.value;
    if (selectedCountryId) {
      this.store.dispatch(loadRegionsFromInstitutionsSettings({countryId: selectedCountryId}));
    }
    this.institutionSettingsForm.controls.regionId.setValue(null);
    this.institutionSettingsForm.controls.cityId.setValue(null);
    this.store.dispatch(resetCities());
  }

  onChangeRegion() {
    this.loadCities();
  }

  onSubmit() {
    if (this.institutionSettingsForm?.valid) {
      const request = this.createUpdateInstitutionSettingRequest();
      this.store.dispatch(
        updateInstitution({
          request: request,
          id: this.selectedOrganization?.id ?? 0,
        })
      );
    }
  }

  private createUpdateInstitutionSettingRequest(): IInstitutionSettingsRequest {
    return {
      instituteId: this.selectedOrganization?.instituteId,
      name: this.institutionSettingsForm.controls.name.value,
      address: this.institutionSettingsForm.controls.address.value,
      countryId: this.institutionSettingsForm.controls.countryId.value,
      regionId: this.institutionSettingsForm.controls.regionId.value,
      cityId: this.institutionSettingsForm.controls.cityId.value,
      zipPostalCode: this.institutionSettingsForm.controls.zipPostalCode.value,
      contractNo: this.selectedOrganization?.contractNo,
      statusId: this.selectedOrganization?.statusId,
    } as IInstitutionSettingsRequest;
  }

  private loadCities() {
    this.institutionSettingsForm.controls.cityId.setValue(null);
    const selectedCountryId = this.institutionSettingsForm.controls.countryId.value;
    const selectedCountyId = this.institutionSettingsForm.controls.regionId.value;
    if (selectedCountryId && selectedCountyId) {
      this.store.dispatch(
        loadCitiesFromInstitutionsSettings({
          countryId: selectedCountryId,
          regionId: selectedCountyId,
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private translateStaticText() {
    this.exceedTestLimit = this.translateService.instant('SignUp.ExceedTestLimit');
    this.allowCharecter = this.translateService.instant('SignUp.AllowCharecter');
    this.allowAlphanumeric = this.translateService.instant('SignUp.AllowAlphanumeric');
    this.allowDigitChar = this.translateService.instant('SignUp.AllowDigitChar');
    this.allowLetterDiacritics = this.translateService.instant('SignUp.AllowLetterDiacritics');
    this.allowAlphaNumericDiacritics = this.translateService.instant('SignUp.AllowAlphaNumericDiacritics');
  }
}
